import { UPGRADES, UPGRADE_CATEGORY_LABELS } from '../../data/upgrades';
import type { UpgradeCategory, UpgradeDef } from '../../data/types';
import { buyUpgrade } from '../../core/actions';
import { describeUpgrade } from '../../core/describe';
import { upgradeCost } from '../../core/economy';
import { firstUnmet, reqProgress, reqText, reqsMet } from '../../core/requirements';
import { game } from '../controller';
import { h, progressBar, setProgress, type Bindings } from '../dom';
import { buyButton, card, purchase, section, tag, type Panel } from '../shop';

const ORDER: UpgradeCategory[] = ['feed', 'click', 'training', 'coop', 'rooster', 'golden', 'wrangler'];
const ICON: Record<UpgradeCategory, string> = { feed: '🌾', training: '🎓', click: '👆', rooster: '📣', golden: '✨', wrangler: '🤠', coop: '🏠' };

function owned(def: UpgradeDef): boolean {
  const s = game.state;
  return def.category === 'coop' ? game.viewFlock().coop.includes(def.id) : s.upgrades.includes(def.id);
}

function available(): UpgradeDef[] {
  const s = game.state;
  return UPGRADES.filter((u) => !owned(u) && reqsMet(s, u.requires));
}

export const upgradesPanel: Panel = {
  id: 'upgrades',
  label: 'Upgrades',
  badge: () => available().filter((u) => game.state.money >= upgradeCost(game.state, u, game.viewFlock().id)).length,
  key: () => {
    const s = game.state;
    return [s.viewFlock, s.upgrades.length, game.viewFlock().coop.length, available().map((u) => u.id).join(','), s.won].join('|');
  },
  build(root: HTMLElement, b: Bindings) {
    const s = game.state;
    const flock = game.viewFlock();
    root.append(h('p', { class: 'hint', text: 'Upgrades apply to the whole farm unless marked THIS FLOCK. Every effect listed is exactly what you get.' }));
    for (const cat of ORDER) {
      const all = UPGRADES.filter((u) => u.category === cat);
      const avail = all.filter((u) => !owned(u) && reqsMet(s, u.requires)).sort((a, x) => a.cost - x.cost);
      const next = all.filter((u) => !owned(u) && !reqsMet(s, u.requires)).sort((a, x) => a.cost - x.cost);
      if (avail.length === 0 && next.length === 0) continue;
      // Training: only show locked tiers for breeds the player owns (no spoilers for breeds you don't have).
      const lockedHint = cat === 'training' ? next.filter((u) => u.requires.every((r) => r.kind !== 'ownBreed')).slice(0, 1) : next.slice(0, 1);
      if (avail.length === 0 && lockedHint.length === 0) continue;
      const sec = section(`${ICON[cat]} ${UPGRADE_CATEGORY_LABELS[cat]}`, cat === 'coop' ? flock.name : undefined);
      for (const u of avail) {
        const buy = buyButton({ cost: () => upgradeCost(game.state, u, flock.id), onBuy: () => purchase(() => buyUpgrade(game.state, u.id, flock.id)) }, b);
        sec.append(card({
          title: [u.name, cat === 'coop' ? tag('this flock', 'flock') : null].filter(Boolean) as (Node | string)[],
          desc: describeUpgrade(u),
          ...(u.flavor ? { flavor: u.flavor } : {}),
          side: [buy.price, buy.btn],
        }));
      }
      for (const u of lockedHint) {
        const req = firstUnmet(s, u.requires);
        const bar = progressBar(0);
        if (req) b.add(() => { const p = reqProgress(game.state, req); setProgress(bar, p.current / p.target); });
        sec.append(card({ cls: 'locked', title: ['🔒 ', u.name], desc: describeUpgrade(u), meta: req ? `Needs: ${reqText(s, req)}` : '', extra: bar }));
      }
      root.append(sec);
    }
    const ownedList = UPGRADES.filter(owned);
    if (ownedList.length) {
      root.append(h('details', { class: 'section' },
        h('summary', {}, `Owned upgrades (${ownedList.length})`),
        ...ownedList.map((u) => h('div', { class: 'meta' }, `✓ ${u.name} — ${describeUpgrade(u)}`))));
    }
  },
};
